import { Loader2 } from 'lucide-react';
import { Suspense, useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

/**
 * The authenticated app shell: a fixed dark rail beside a light working area.
 *
 * Below `lg` the rail becomes a slide-over. It is the SAME component at every
 * width rather than a separate mobile nav, so navigation state and permission
 * filtering live in exactly one place — a second nav is a second thing to keep
 * in sync, and it always drifts.
 */
export function AppLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const close = useCallback(() => setNavOpen(false), []);

  // A route change means the drawer has done its job. Without this, tapping a
  // link leaves the drawer covering the page it just navigated to.
  useEffect(() => close(), [location.pathname, close]);

  // Escape closes it: the drawer is modal on small screens, and a modal thing
  // that cannot be dismissed from the keyboard is a trap.
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen, close]);

  return (
    <div className="min-h-dvh bg-background">
      {/* Rendered only while open, so it can never swallow clicks when closed. */}
      {navOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={close}
          className="fixed inset-0 z-30 bg-[rgba(42,52,112,0.4)] backdrop-blur-[2px] lg:hidden"
        />
      ) : null}

      {/* No aria-hidden here: the same element is the DOCKED rail from lg up,
          where it is visible and must stay in the accessibility tree. Hiding it
          on `!navOpen` would erase the primary navigation on every desktop. The
          residual issue — off-screen links remain focusable on small screens
          while the drawer is closed — needs a viewport-aware fix, not a static
          attribute. */}
      <aside className={cnDrawer(navOpen)}>
        <Sidebar onNavigate={close} />
      </aside>

      <div className="min-w-0 lg:pl-[256px]">
        <Topbar onMenuToggle={() => setNavOpen((v) => !v)} />
        <main className="w-full min-w-0 p-4 sm:p-6">
          <div key={location.pathname} className="page-enter w-full max-w-[1400px]">
            <Suspense
              fallback={
                <div className="grid place-items-center py-24">
                  <Loader2 className="size-5 animate-spin text-primary" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

/** Off-canvas below lg, permanently docked from lg up. */
function cnDrawer(open: boolean): string {
  return [
    'fixed inset-y-0 left-0 z-40 w-[256px] transition-transform duration-300 ease-in-out',
    open ? 'translate-x-0' : '-translate-x-full',
    'lg:translate-x-0',
  ].join(' ');
}
